import React, {Component} from "react";
import moment from "moment";
import {DayPicker} from "react-day-picker";
import "react-day-picker/dist/style.css";
import {BU_PERIOD_TEMPORARY} from "../../periods";
import {DatetimeHelper} from "../../utils/DatetimeHelper";
import { withTranslation } from 'react-i18next';


class BusinessUnitPeriodFieldClass extends Component {
    /*
    *   The onChange prop is required.
    * */

    static dateFormat = 'DD/MM/YYYY';
    static temporalPeriodType = 'TEMPORARY';
    static periodicPeriodType = 'PERIODIC';

    static defaultSelectedType = BusinessUnitPeriodFieldClass.periodicPeriodType;
    static defaultSelectedPeriodicRangeDefinition = {first_day: '1'};

    static defaultSelectedPeriod() {
        return BusinessUnitPeriodFieldClass.selectedPeriodWith(
            BusinessUnitPeriodFieldClass.defaultSelectedType,
            BusinessUnitPeriodFieldClass.defaultSelectedPeriodicRangeDefinition)
    }

    static selectedPeriodWith(type, rangeDefinition) {
        return {type: type, range_definition: rangeDefinition}
    }

    constructor(props) {
        super(props);
        this.state = {
            focusedInput: null,
            selectedPeriodType: BusinessUnitPeriodFieldClass.defaultSelectedType,
            selectedPeriodicRangeDefinition: BusinessUnitPeriodFieldClass.defaultSelectedPeriodicRangeDefinition,
            selectedTemporalRangeDefinition: this.dateRangeFrom(moment(), moment()),
            isDatePickerOpen: false,
        };

        this.handleSelectedPeriodType = this.handleSelectedPeriodType.bind(this);
        this.handleSelectedPeriodicRangeDefinition = this.handleSelectedPeriodicRangeDefinition.bind(this);
        this.handleSelectedTemporalRangeDefinition = this.handleSelectedTemporalRangeDefinition.bind(this);
        this.datePickerRef = React.createRef();
        this.handleClickOutside = this.handleClickOutside.bind(this);
    }

    componentDidMount() {
        document.addEventListener('mousedown', this.handleClickOutside);
    }

    componentWillUnmount() {
        document.removeEventListener('mousedown', this.handleClickOutside);
    }

    handleClickOutside(event) {
        if (this.datePickerRef.current && !this.datePickerRef.current.contains(event.target)) {
            this.setState({ isDatePickerOpen: false });
        }
    }

    // Events
    handleSelectedPeriodType(event) {
        event.preventDefault();

        const type = event.target.value;
        this.setState({selectedPeriodType: type});

        const rangeDefinition = this.rangeDefinitionFor(type);
        this.selectedPeriodChangedWith(type, rangeDefinition)
    }

    handleSelectedPeriodicRangeDefinition(event) {
        event.preventDefault();
        let rangeDefinition = {first_day: event.target.value};
        const periodType = BusinessUnitPeriodFieldClass.periodicPeriodType;
        this.setState({selectedPeriodType: periodType, selectedPeriodicRangeDefinition: rangeDefinition});
        this.selectedPeriodChangedWith(periodType, rangeDefinition)
    }

    handleSelectedTemporalRangeDefinition(range, selectedDay) {
        const from = this.state.selectedTemporalRangeDefinition.from_date;
        const to = this.state.selectedTemporalRangeDefinition.to_date;
        const clickedMoment = moment(selectedDay);

        let newFrom = from;
        let newTo = to;

        // Logic to handle the three states of date range selection
        if (from && to) {
            // State 3: A full range is selected, so start a new selection
            newFrom = clickedMoment;
            newTo = null;
        } else if (from && !to) {
            // State 2: A start date is selected, but no end date
            if (clickedMoment.isAfter(from)) {
                newTo = clickedMoment;
            } else {
                newFrom = clickedMoment; // Allow changing start date
            }
        } else {
            // State 1: No date is selected, so this is the start date
            newFrom = clickedMoment;
        }

        const newRangeDefinition = this.dateRangeFrom(newFrom, newTo);
        this.setState({
            selectedPeriodType: BU_PERIOD_TEMPORARY,
            selectedTemporalRangeDefinition: newRangeDefinition
        });

        // Only trigger updates and close popover when a complete range is selected
        if (newFrom && newTo) {
            const currentDate = moment();
            if (newTo.isBefore(currentDate, 'day')) {
                const { t } = this.props;
                alert(t('business_unit_period_field.cannot_select_past_period'));
                // Revert to a default valid state if the selected period is in the past
                const { firstDate, lastDate } = DatetimeHelper.calculateDatesForMonthlyPeriod(1);
                this.setState({
                    selectedTemporalRangeDefinition: this.dateRangeFrom(firstDate, lastDate),
                    isDatePickerOpen: false
                });
                return;
            }
            const dateRangeDefinition = this.parsedDateRangeFrom(newFrom, newTo);
            this.selectedPeriodChangedWith(BU_PERIOD_TEMPORARY, dateRangeDefinition);
            this.setState({ isDatePickerOpen: false });
        }
    }

    selectedPeriodChangedWith(type, rangeDefinition) {
        let period = BusinessUnitPeriodFieldClass.selectedPeriodWith(type, rangeDefinition);
        this.props.onChange(period)
    }

    rangeDefinitionFor(type) {
        let rangeDefinition;
        if (type === BusinessUnitPeriodFieldClass.temporalPeriodType) {
            let fromDatetime = this.state.selectedTemporalRangeDefinition.from_date;
            let toDatetime = this.state.selectedTemporalRangeDefinition.to_date;
            rangeDefinition = this.parsedDateRangeFrom(fromDatetime, toDatetime);
        } else {
            rangeDefinition = this.state.selectedPeriodicRangeDefinition;
        }
        return rangeDefinition;
    }

    // Rendering

    render() {
        const { t } = this.props;
        return (
            <div className="row g-2 align-items-start" style={{ position: 'relative', zIndex: 1051 }}>
                <div className="col-6">
                    <select name="periodType" onChange={this.handleSelectedPeriodType} className="form-select">
                        <option value={BusinessUnitPeriodFieldClass.periodicPeriodType}>{t('business_unit_period_field.monthly')}</option>
                        <option value={BusinessUnitPeriodFieldClass.temporalPeriodType}>{t('business_unit_period_field.temporary')}</option>
                    </select>
                </div>
                <div className="col-6">
                    {this.renderRangeDefinition()}
                </div>
            </div>
        )
    }

    renderRangeDefinition() {
        if (this.state.selectedPeriodType === BusinessUnitPeriodFieldClass.temporalPeriodType)
            return this.renderTemporalRangeDefinition();
        else
            return this.renderPeriodicRangeDefinition();
    }

    renderPeriodicRangeDefinition() {
        if(this.props.hidePeriodicRangeDefinition === true)
            return;

        const { t } = this.props;
        const defaultValue = this.state.selectedPeriodicRangeDefinition.first_day.toString();

        return (
            <div>
                <select name="periodicRangeDefinition"
                       className="form-select"
                       onChange={this.handleSelectedPeriodicRangeDefinition} defaultValue={defaultValue}>
                    {this.renderDayOptions()}
                </select>
                <p className="field-footnote pb-0 mb-0">{t('business_unit_period_field.choose_day_help_text')}</p>
            </div>
        )
    }

    renderDayOptions() {
        const optionCreation = this.optionForDayNumber.bind(this);
        return this.daysNumbers().map(optionCreation)
    }

    dayIsInvalid = (aDay) => {
        // Calculate the minimum allowed date (3 months ago from today)
        const minDate = new Date();
        minDate.setMonth(minDate.getMonth() - 3);
        return minDate > aDay;
    };

    renderTemporalRangeDefinition() {
        const fromDate = this.state.selectedTemporalRangeDefinition.from_date;
        const toDate = this.state.selectedTemporalRangeDefinition.to_date;
        const fromString = fromDate ? fromDate.format('DD MMM YYYY') : '';
        const toString = toDate ? toDate.format('DD MMM YYYY') : '';

        return (
            <div className="period-date-picker p-0" style={{ position: 'relative', height: '100%' }}>
                <div className="form-control form-select" onClick={() => this.setState({ isDatePickerOpen: !this.state.isDatePickerOpen })}>
                    <span>{fromString}</span>
                    <span style={{ margin: '0 10px' }}>→</span>
                    <span>{toString}</span>
                </div>
                {this.state.isDatePickerOpen && (
                    <div ref={this.datePickerRef} className="date-picker-popover" style={{ 
                        position: 'absolute', 
                        zIndex: 10, 
                        top: '100%', 
                        left: '50%', 
                        transform: 'translateX(-50%)', 
                        background: 'white', 
                        border: '1px solid #ccc', 
                        borderRadius: '5px', 
                        marginTop: '5px',
                        boxShadow: '0 2px 5px rgba(0,0,0,0.15)',
                        width: '680px',
                        maxWidth: '95vw',
                        overflow: 'hidden'
                    }}>
                        <DayPicker
                            mode="range"
                            selected={{
                                from: fromDate ? fromDate.toDate() : undefined,
                                to: toDate ? toDate.toDate() : undefined
                            }}
                            onSelect={(range, selectedDay) => {
                                this.handleSelectedTemporalRangeDefinition(range, selectedDay);
                            }}
                            disabled={this.dayIsInvalid}
                            numberOfMonths={2}
                            className="add-budget-calendar"
                            defaultMonth={fromDate.toDate()}
                            classNames={{
                                day_range_start: 'rdp-day_range_start',
                                day_range_end: 'rdp-day_range_end',
                                day_selected: 'rdp-day_selected',
                                day_range_middle: 'rdp-day_range_middle'
                            }}
                        />
                    </div>
                )}
            </div>
        );
    }

    // Private -- Support

    optionForDayNumber(dayNumber) {
        return <option key={dayNumber} value={dayNumber}> {this.padDayNumber(dayNumber)}</option>

    }

    daysNumbers() {
        /*
            Answer an array with numbers from 1 to 31
         */
        return Array(31).fill(0).map((_, i) => i + 1);
    }

    padDayNumber(dayNumber) {
        return dayNumber.toString().padStart(2, '0');
    }

    parsedDateRangeFrom(fromDate, toDate) {
        let format = BusinessUnitPeriodFieldClass.dateFormat;
        const fromDateString = this.formatDate(fromDate, format);
        const toDateString = this.formatDate(toDate, format);
        return {from_date: fromDateString, to_date: toDateString}
    }

    formatDate(aDate, dateFormat) {
        try {
            return aDate.format(dateFormat);
        } catch (TypeError) {
            return ''
        }
    }

    dateRangeFrom(fromDate, toDate) {
        return {from_date: fromDate, to_date: toDate}
    }

}

export { BusinessUnitPeriodFieldClass };
export const BusinessUnitPeriodField = withTranslation()(BusinessUnitPeriodFieldClass);
export default BusinessUnitPeriodField;
